import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tween } from 'react-gsap';
import { Trans, t } from '@lingui/macro';
import GemsAnimation from './gems-animation';
import css from './congratulations.scss';
import CongratulationsText from './congratulations-text';
import { COIN_DELAY, COIN_DURATION, EASE, TEXT_DURATION, TEXT_OFFSET } from './congratulations-utils';
import { useLocalisation } from '../../helpers/i18n/use-localisation';

const ANNOUNCEMENT_ID = 'congratulations-announcement';
const FOCUS_DELAY = 200;

const Congratulations = ({ description, stepCount }) => {
  const { i18n } = useLocalisation();
  const [announcement, setAnnouncement] = useState('');
  const containerRef = useRef(null);

  const meritsEarned = stepCount === 1 ?
    i18n._(t`You have earned 1 Merit to use in Meritopia!`) :
    i18n._(t`You have earned ${stepCount} Merits to use in Meritopia!`)
      .replace('{stepCount}', stepCount);

  useEffect(() => {
    if (!stepCount) {
      return undefined;
    }

    setAnnouncement(
      i18n._(t`Congratulations! You have mastered ${description}. ${meritsEarned}`)
        .replace('{description}', description)
        .replace('{meritsEarned}', meritsEarned)
    );

    const timer = window.setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.focus();
      }
    }, FOCUS_DELAY);

    return () => window.clearTimeout(timer);
  }, [description, stepCount, meritsEarned]);

  if (!stepCount) {
    return null;
  }

  return (
    <div
      className={css.congratulations}
      ref={containerRef}
      tabIndex={-1}
      aria-labelledby={ANNOUNCEMENT_ID}
    >
      <div id={ANNOUNCEMENT_ID} className={css.announcement}>
        {announcement}
      </div>
      <div className={css.curtains}>
        <Tween
          from={{
            y: -TEXT_OFFSET
          }}
          to={{
            y: 0
          }}
          duration={TEXT_DURATION}
          ease={EASE}
        >
          <div className={css.top} />
        </Tween>
        <Tween
          from={{
            y: TEXT_OFFSET
          }}
          to={{
            y: 0
          }}
          duration={TEXT_DURATION}
          ease={EASE}
        >
          <div className={css.bottom} />
        </Tween>
      </div>
      <div className={css.content}>
        <CongratulationsText />
        <Tween
          from={{
            opacity: 0
          }}
          to={{
            opacity: 1
          }}
          duration={TEXT_DURATION}
          ease={EASE}
        >
          <h4 className={css.toast}>
            <Trans>You have mastered</Trans><br />
            <strong>{description}</strong><br />
            {meritsEarned}
          </h4>
        </Tween>
        {
          stepCount && <GemsAnimation stepCount={stepCount} />
        }
        <br />
        <Tween
          from={{
            opacity: 0
          }}
          to={{
            opacity: 1
          }}
          duration={COIN_DURATION}
          delay={TEXT_DURATION + (stepCount * (COIN_DELAY + COIN_DURATION))}
          ease={EASE}
        >
          <h4 className={css.toast}>
            <Trans>Press <strong>Save &amp; Close</strong> to exit</Trans>
          </h4>
        </Tween>
      </div>
    </div>
  );
};

Congratulations.propTypes = {
  description: PropTypes.string,
  stepCount: PropTypes.number
};

export default Congratulations;
